import React from 'react';
import { Share2, Check, Instagram } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import logoAsset from '../../assets/logo-asset.png';

interface FooterProps {
  setCurrentPage: (page: string) => void;
  onFeatureClick: (tabIndex: number) => void;
}

export default function Footer({ setCurrentPage, onFeatureClick }: FooterProps) {
  const { t, i18n } = useTranslation();
  const [copied, setCopied] = React.useState(false);

  const handleNavigate = (page: string) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Catat Crypto',
          text: t('footer.desc') || '',
          url: window.location.origin,
        });
      } catch (error) {
      }
    } else {
      try {
        await navigator.clipboard.writeText(window.location.origin);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
      }
    }
  };

  return (
    <footer className="bg-[#120505] border-t border-[#2A0D0B] pt-20 pb-8 mt-auto">
      <div className="max-w-[1200px] mx-auto px-6">
        <div className="flex flex-col md:flex-row justify-between mb-20 gap-12">
          {/* Logo & Description */}
          <div className="w-full md:w-1/3 text-left">
            <div className="flex items-center mb-6 overflow-hidden">
              <img src={logoAsset} alt="Catat Crypto" className="h-12 w-auto object-contain" />
            </div>
            <p className="text-[#8C8C8C] text-sm leading-relaxed mb-8 pr-8">
              {t('footer.desc')}
            </p>
            <div className="flex flex-wrap items-center gap-3">
               <button 
                 onClick={handleShare}
                 className="w-10 h-10 rounded-full bg-[#1A0A0A] border border-[#2F0B0B] flex items-center justify-center text-[#8C8C8C] hover:text-white transition-colors"
                 aria-label="Bagikan"
               >
                  {copied ? <Check className="w-4 h-4 text-green-500" /> : <Share2 className="w-4 h-4" />}
               </button>
               <a 
                 href="https://www.tiktok.com/@catatcrypto"
                 target="_blank"
                 rel="noopener noreferrer"
                 className="w-10 h-10 rounded-full bg-[#1A0A0A] border border-[#2F0B0B] flex items-center justify-center text-[#8C8C8C] hover:text-white transition-colors"
                 aria-label="TikTok"
               >
                  <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4"><path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1V9.01a6.33 6.33 0 0 0-.79-.05 6.34 6.34 0 0 0-6.34 6.34 6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.33-6.34V8.69a8.18 8.18 0 0 0 4.79 1.52V6.76a4.85 4.85 0 0 1-1.02-.07z"/></svg>
               </a>
               <a 
                 href="https://www.instagram.com/catatcrypto"
                 target="_blank"
                 rel="noopener noreferrer"
                 className="w-10 h-10 rounded-full bg-[#1A0A0A] border border-[#2F0B0B] flex items-center justify-center text-[#8C8C8C] hover:text-white transition-colors"
                 aria-label="Instagram"
               >
                  <Instagram className="w-4 h-4" />
               </a>
            </div>
          </div>

          {/* Links */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-8 md:gap-16 text-left">
             <div>
               <h4 className="text-white font-bold mb-6">{t('footer.product')}</h4>
               <ul className="space-y-4">
                 <li><a href="#" onClick={(e) => { e.preventDefault(); onFeatureClick(0); }} className="text-[#8C8C8C] hover:text-white text-sm transition-colors">{t('appMockup.tabs.dashboard')}</a></li>
                 <li><a href="#" onClick={(e) => { e.preventDefault(); onFeatureClick(1); }} className="text-[#8C8C8C] hover:text-white text-sm transition-colors">{t('appMockup.tabs.reports')}</a></li>
                 <li><a href="#" onClick={(e) => { e.preventDefault(); onFeatureClick(2); }} className="text-[#8C8C8C] hover:text-white text-sm transition-colors">{t('appMockup.tabs.calendar')}</a></li>
                 <li><a href="#" onClick={(e) => { e.preventDefault(); onFeatureClick(3); }} className="text-[#8C8C8C] hover:text-white text-sm transition-colors">{t('appMockup.tabs.trades')}</a></li>
                 <li><a href="#" onClick={(e) => { e.preventDefault(); onFeatureClick(4); }} className="text-[#8C8C8C] hover:text-white text-sm transition-colors">{t('appMockup.tabs.notebook')}</a></li>
                 <li><a href="#" onClick={(e) => { e.preventDefault(); onFeatureClick(5); }} className="text-[#8C8C8C] hover:text-white text-sm transition-colors">{t('appMockup.tabs.ai')}</a></li>
               </ul>
             </div>
             <div>
               <h4 className="text-white font-bold mb-6">{t('footer.company')}</h4>
               <ul className="space-y-4">
                 <li><a href="#" onClick={(e) => { e.preventDefault(); handleNavigate('about'); }} className="text-[#8C8C8C] hover:text-white text-sm transition-colors">{t('nav.about')}</a></li>
                 <li><a href="/partnership" className="text-[#8C8C8C] hover:text-white text-sm transition-colors">{t('footer.becomePartner')}</a></li>
                 <li><a href="https://docs.catatcrypto.com/getting-started/introduction/" target="_blank" rel="noopener noreferrer" className="text-[#8C8C8C] hover:text-white text-sm transition-colors">{t('footer.docs')}</a></li>
               </ul>
             </div>
             <div>
               <h4 className="text-white font-bold mb-6">{t('footer.legal')}</h4>
               <ul className="space-y-4">
                 <li><a href="#" onClick={(e) => { e.preventDefault(); handleNavigate('privacy'); }} className="text-[#E26A59] hover:text-[#ff7a66] text-sm transition-colors font-medium">Privacy Policy</a></li>
                 <li><a href="#" onClick={(e) => { e.preventDefault(); handleNavigate('terms'); }} className="text-[#8C8C8C] hover:text-white text-sm transition-colors">Terms &amp; Conditions</a></li>
               </ul>
             </div>
          </div>
        </div>

        {/* Bottom Line */}
        <div className="pt-8 border-t border-[#2A0D0B] flex flex-col md:flex-row justify-between items-center gap-4">
           <p className="text-[#666666] text-sm">{t('footer.rights')}</p>
           <div className="flex gap-6">
             <button onClick={() => { i18n.changeLanguage('en'); localStorage.setItem('language', 'en'); }} className={`text-sm transition-colors font-medium ${i18n.language === 'en' ? 'text-[#E26A59]' : 'text-[#666666] hover:text-white'}`}>English</button>
             <button onClick={() => { i18n.changeLanguage('id'); localStorage.setItem('language', 'id'); }} className={`text-sm transition-colors font-medium ${i18n.language === 'id' ? 'text-[#E26A59]' : 'text-[#666666] hover:text-white'}`}>Indonesia</button>
           </div>
        </div>
      </div>
    </footer>
  );
}
